import React from 'react';
import { keepPreviousData } from '@tanstack/react-query';
import { useOrganization } from '@/contexts/OrganizationContext';
import { trpc, type RouterOutputs } from '@/lib/trpc';
import type { Product } from '../types';

type PurchaseableSkuItem = RouterOutputs['inventory']['listSkusForPurchaseOrder'][number];

// Hook for the PO creation / edit SKU picker.
//
// Unlike useInventoryQueries (which hides SKUs that have never been stocked at
// a location), this returns the full org catalog with zero-stock placeholders
// for SKUs that don't yet have an sku_inventory row at the target location.
// That's what unblocks creating a PO against a brand-new location (BLU-349) —
// backend auto-activates the real inventory rows on PO submit.
export function usePurchaseableSkus(locationId: string | null | undefined) {
  const { organization } = useOrganization();

  const {
    data: rawProducts,
    isLoading: loading,
    isFetching,
    error,
    refetch,
  } = trpc.inventory.listSkusForPurchaseOrder.useQuery(
    { locationId: locationId ?? '' },
    {
      enabled: !!organization && !!locationId,
      staleTime: 30000,
      retry: 2,
      placeholderData: keepPreviousData,
    }
  );

  const products: Product[] = React.useMemo(
    () =>
      rawProducts?.map((item: PurchaseableSkuItem) => ({
        id: item.id,
        sku: item.sku,
        title: item.title ?? undefined,
        category: item.category || { type: undefined, size: undefined, color: undefined },
        stockLevel: item.stockLevel,
        onHand: item.onHand,
        available: item.available,
        committed: item.committed,
        unavailable: item.unavailable,
        incoming: item.incoming,
        image_url: item.imageUrl ?? undefined,
        notify: item.notify ?? undefined,
        updated_at: item.updatedAt,
        categoryCostCurrency: item.categoryCostCurrency ?? undefined,
        categoryCostSourceAmount: item.categoryCostSourceAmount ?? undefined,
        skuCostOverride: item.skuCostOverride ?? null,
        effectiveCost: item.effectiveCost ?? undefined,
        weightedAverageCost: item.weightedAverageCost ?? undefined,
        isLocked: item.isLocked ?? undefined,
        productionType: item.productionType ?? 'POD',
        isKeyItem: item.isKeyItem ?? false,
        isPreprinted: item.isPreprinted ?? false,
        effectiveSupplierId: item.effectiveSupplierId ?? null,
        backupSupplierId: item.backupSupplierId ?? null,
      })) ?? [],
    [rawProducts]
  );

  return {
    products,
    loading: (loading && !rawProducts) || !organization,
    isFetching,
    error: error as Error | null,
    refetch,
  };
}
